import clsx from 'clsx';
import { memo } from 'react';

import { TranscriptionSegment } from '@/services/types/transcript-types';

interface TranscriptSegmentProps {
  segment: TranscriptionSegment;
  isMatch: boolean;
  isPlaying: boolean;
  audioIsPlaying: boolean;
  speakerBgColor: string;
  speakerTextColor: string;
  formattedStartTime: string;
  onInteract: (segmentId: string, event?: React.KeyboardEvent) => void;
  highlightText: (text: string, query: string) => string;
  searchQuery: string;
}

/**
 * Renders a single transcript segment row.
 * Kept deliberately small & memoized for performance.
 */
export const TranscriptSegment = memo(
  ({
    segment,
    isMatch,
    isPlaying,
    audioIsPlaying,
    speakerBgColor,
    speakerTextColor,
    formattedStartTime,
    onInteract,
    highlightText,
    searchQuery,
  }: TranscriptSegmentProps) => {
    return (
      <div
        role="button"
        tabIndex={0}
        aria-pressed={isPlaying}
        onClick={() => onInteract(segment.id)}
        onKeyDown={(e) => onInteract(segment.id, e)}
        className={clsx(
          'group cursor-pointer transition-all duration-200 ease-out',
          'focus:outline-none  rounded-lg',
          {
            'transform scale-[1.02]': isPlaying,
          },
        )}
      >
        <div className="flex items-start gap-4">
          {/* Timestamp */}
          <div
            className={clsx(
              'text-xs font-mono text-gray-400 w-12 flex-shrink-0 pt-3 transition-colors duration-200',
              'group-hover:text-gray-600',
              {
                'text-blue-500 font-medium': isPlaying,
              },
            )}
          >
            {formattedStartTime}
          </div>

          {/* Content */}
          <div
            className={clsx(
              'flex-1 rounded-lg px-4 py-3 transition-all duration-200 ease-out border',
              speakerBgColor,
              'border-transparent',
              'group-hover:shadow-sm group-hover:border-gray-100',
              {
                'ring-2 ring-yellow-200 ring-opacity-60': isMatch,
                'shadow-md border-blue-200 ring-2 ring-blue-100': isPlaying,
              },
            )}
          >
            {/* Speaker Header */}
            <div className={clsx('flex items-center justify-between mb-2')}>
              <span
                className={clsx(
                  'text-xs font-semibold tracking-wide',
                  speakerTextColor,
                )}
              >
                {segment.external_speaker_label || 'Speaker'}
              </span>

              <div className="flex items-center gap-2">
                {isMatch && (
                  <span className="text-xs text-yellow-600 font-medium bg-yellow-100 px-1.5 py-0.5 rounded">
                    Match
                  </span>
                )}
                {audioIsPlaying && isPlaying && (
                  <span className="text-xs text-blue-600 font-medium flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                    Playing
                  </span>
                )}
              </div>
            </div>

            {/* Transcript Text */}
            {/* eslint-disable-next-line react/no-danger */}
            <p
              className="text-sm text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: highlightText(segment.text, searchQuery),
              }}
            />
          </div>
        </div>
      </div>
    );
  },
);

TranscriptSegment.displayName = 'TranscriptSegment';
